"use client"

import { useRef, useState, useEffect } from "react"

/**
 * Thin client wrapper that manages IntersectionObserver and exposes
 * visibility state via data attributes. Keeps parent server components
 * server-rendered while enabling CSS-driven scroll animations.
 *
 * Usage: wrap server-rendered content, then target descendants with
 * CSS selectors like [data-anim-ready="true"]:not([data-visible="true"]) .item
 */
export function ScrollReveal({
    children,
    className,
    style,
    threshold = 0.2,
    rootMargin = "0px 0px -18% 0px",
}: {
    children: React.ReactNode
    className?: string
    style?: React.CSSProperties
    threshold?: number
    rootMargin?: string
}) {
    const ref = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(false)
    const [animReady, setAnimReady] = useState(false)

    useEffect(() => { setAnimReady(true) }, [])

    // Toggles on/off for replay — no disconnect in callback
    useEffect(() => {
        const el = ref.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => { setVisible(entry.isIntersecting) },
            { threshold, rootMargin }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [threshold, rootMargin])

    const show = !animReady || visible

    return (
        <div
            ref={ref}
            className={className}
            style={style}
            data-visible={show}
            data-anim-ready={animReady}
        >
            {children}
        </div>
    )
}
